/**
 * Who a "Notify staff" step / action reaches (TRUS-686): the assignee, the
 * member's case manager, everyone with given roles, and named people. The
 * server resolves these to staff in the org and de-duplicates them.
 *
 * Roles come from the org's role list (`GET /api/cbos/{cbo}/all-roles`,
 * value = role_type); people from the org staff list.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { StaffMultiSelect, type StaffMultiSelectOption } from '@/components/forms/StaffMultiSelect';
import { useOrgStaffOptions } from '@/components/forms/staffOptions';
import { userManagementAPI } from '@/services/api/userManagement';
import type { StaffRecipients } from './notifySteps';

export const orgRolesKey = (cboId: number) => ['notify-org-roles', cboId] as const;

/** The org's roles as picker options (role_type → display name). */
export function useOrgRoleOptions(): StaffMultiSelectOption[] {
  const { language } = useAppTranslation();
  const cboId = Number(typeof localStorage !== 'undefined' ? localStorage.getItem('active_cbo_id') || 0 : 0);
  const query = useQuery({
    queryKey: orgRolesKey(cboId),
    queryFn: () => userManagementAPI.getAllOrgRoles(cboId),
    enabled: cboId > 0,
    staleTime: 5 * 60_000,
  });
  const rows = Array.isArray(query.data) ? query.data : [];
  const out: StaffMultiSelectOption[] = [];
  for (const r of rows) {
    const value = String(r?.role_type || '').trim();
    if (!value || out.some((o) => o.value === value)) continue;
    const label = (language === 'es' && r.display_name_es) || r.display_name_en || value;
    out.push({ label, value });
  }
  return out;
}

interface StaffRecipientsEditorProps {
  idPrefix: string;
  value: StaffRecipients;
  onChange: (next: StaffRecipients) => void;
  disabled?: boolean;
  /** Offer "the case manager" (member workflows only). */
  allowCaseManager?: boolean;
  /** Label for the "assignee" checkbox (a step's assignee vs. an event's). */
  assigneeLabel?: string;
}

export function StaffRecipientsEditor({
  idPrefix,
  value,
  onChange,
  disabled,
  allowCaseManager = true,
  assigneeLabel,
}: StaffRecipientsEditorProps) {
  const { t } = useAppTranslation();
  const roleOptions = useOrgRoleOptions();
  const staffOptions = useOrgStaffOptions({ includeOrgAdmins: true });

  const pickerText = {
    searchPlaceholder: t('workflows.notify.search', 'Search…'),
    clearLabel: t('workflows.notify.clear', 'Clear all'),
    noMatchLabel: (q: string) => t('workflows.notify.noMatch', 'No match for “{{query}}”', { query: q }),
    removeLabel: (label: string) => t('workflows.notify.remove', 'Remove {{name}}', { name: label }),
  };

  return (
    <fieldset className="space-y-3" data-testid={`${idPrefix}-recipients`}>
      <legend className="mb-1 text-sm font-medium">{t('workflows.notify.recipients', 'Recipients')}</legend>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 accent-[var(--primary,#2563EB)]"
          checked={value.assignee === true}
          onChange={(e) => onChange({ ...value, assignee: e.target.checked })}
          disabled={disabled}
          data-testid={`${idPrefix}-recipient-assignee`}
        />
        {assigneeLabel ?? t('workflows.notify.assignee', 'Whoever this is assigned to')}
      </label>
      {allowCaseManager && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--primary,#2563EB)]"
            checked={value.case_manager === true}
            onChange={(e) => onChange({ ...value, case_manager: e.target.checked })}
            disabled={disabled}
            data-testid={`${idPrefix}-recipient-case-manager`}
          />
          {t('workflows.notify.caseManager', 'The member’s case manager')}
        </label>
      )}
      <div className="space-y-1">
        <span className="block text-xs font-medium text-muted-foreground">{t('workflows.notify.roles', 'Everyone with these roles')}</span>
        <StaffMultiSelect
          options={roleOptions}
          value={value.roles ?? []}
          onChange={(next) => onChange({ ...value, roles: next })}
          disabled={disabled}
          ariaLabel={t('workflows.notify.roles', 'Everyone with these roles')}
          placeholder={t('workflows.notify.noRoles', 'No roles')}
          emptyHint={t('workflows.notify.rolesEmpty', 'No roles found')}
          {...pickerText}
        />
      </div>
      <div className="space-y-1">
        <span className="block text-xs font-medium text-muted-foreground">{t('workflows.notify.people', 'These people')}</span>
        <StaffMultiSelect
          options={staffOptions}
          value={(value.user_ids ?? []).map(String)}
          onChange={(next) => onChange({ ...value, user_ids: next.map(Number).filter((n) => n > 0) })}
          disabled={disabled}
          ariaLabel={t('workflows.notify.people', 'These people')}
          placeholder={t('workflows.notify.noPeople', 'No one else')}
          emptyHint={t('workflows.notify.peopleEmpty', 'No staff found')}
          {...pickerText}
        />
      </div>
    </fieldset>
  );
}

export default StaffRecipientsEditor;
